import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../services/supabase';

export interface FilterOptions {
  jours: string[];
  creneaux: string[];
  langues: string[];
  sexes: string[];
  filieres: { filiere: string; count: number }[];
  matieres: { matiere: string; count: number }[];
  tarifStats: {
    min: number;
    max: number;
    avg: number;
  };
}

export interface UseFilterOptionsReturn {
  options: FilterOptions;
  loading: boolean;
  error: string | null;
  refreshOptions: () => void;
}

// Options par défaut en cas d'erreur (minimal)
const DEFAULT_OPTIONS: FilterOptions = {
  jours: ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche'],
  creneaux: ['matin', 'apm', 'soir'],
  langues: ['francais', 'anglais'],
  sexes: ['homme', 'femme', 'non_specifie'],
  filieres: [],
  matieres: [],
  tarifStats: { min: 20, max: 80, avg: 40 }
};

export const useFilterOptions = (): UseFilterOptionsReturn => {
  const [options, setOptions] = useState<FilterOptions>(DEFAULT_OPTIONS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchOptions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      // Récupérer les filières disponibles depuis la vue publique
      const { data: filieresData, error: filieresError } = await supabase
        .from('tuteurs_public')
        .select('filiere')
        .not('filiere', 'is', null);

      if (filieresError) {
        throw new Error(`Erreur filières: ${filieresError.message}`);
      }

      // Compter les filières
      const filieresCount: Record<string, number> = {};
      filieresData?.forEach(item => {
        if (item.filiere) {
          filieresCount[item.filiere] = (filieresCount[item.filiere] || 0) + 1;
        }
      });

      const filieres = Object.entries(filieresCount)
        .map(([filiere, count]) => ({ filiere, count }))
        .sort((a, b) => b.count - a.count || a.filiere.localeCompare(b.filiere));

      // Récupérer les matières disponibles depuis la vue publique
      const { data: matieresData, error: matieresError } = await supabase
        .from('tuteurs_public')
        .select('matieres')
        .not('matieres', 'is', null);

      if (matieresError) {
        throw new Error(`Erreur matières: ${matieresError.message}`);
      }

      // Traiter les matières (séparées par virgules)
      const matieresCount: Record<string, number> = {};
      matieresData?.forEach(item => {
        if (item.matieres) {
          const matieresList = item.matieres.split(',').map((m: string) => m.trim().toLowerCase());
          matieresList.forEach((matiere: string) => {
            if (matiere) {
              matieresCount[matiere] = (matieresCount[matiere] || 0) + 1;
            }
          });
        }
      });

      const matieres = Object.entries(matieresCount)
        .map(([matiere, count]) => ({ matiere, count }))
        .sort((a, b) => b.count - a.count || a.matiere.localeCompare(b.matiere));

      // Récupérer les statistiques de tarifs depuis la vue publique
      const { data: tarifsData, error: tarifsError } = await supabase
        .from('tuteurs_public')
        .select('tarif')
        .not('tarif', 'is', null);

      if (tarifsError) {
        throw new Error(`Erreur tarifs: ${tarifsError.message}`);
      }

      const tarifs = tarifsData?.map(item => item.tarif).filter(t => t != null) || [];
      const tarifStats = {
        min: tarifs.length > 0 ? Math.min(...tarifs) : 20,
        max: tarifs.length > 0 ? Math.max(...tarifs) : 80,
        avg: tarifs.length > 0 ? Math.round(tarifs.reduce((a, b) => a + b, 0) / tarifs.length * 100) / 100 : 40
      };

      // Construire l'objet options avec les vraies données
      const newOptions: FilterOptions = {
        jours: DEFAULT_OPTIONS.jours,
        creneaux: DEFAULT_OPTIONS.creneaux,
        langues: DEFAULT_OPTIONS.langues,
        sexes: DEFAULT_OPTIONS.sexes,
        filieres,
        matieres,
        tarifStats
      };

      setOptions(newOptions);
      
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la récupération des options de filtrage');
      // En cas d'erreur, utiliser les options par défaut (mais vides pour filieres/matieres)
      setOptions(DEFAULT_OPTIONS);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOptions();
  }, [fetchOptions]);

  return {
    options,
    loading,
    error,
    refreshOptions: fetchOptions
  };
}; 